/**
 * Jaipur Rugs — Wishlist v3
 * Horizon theme compatible.
 *
 * Key fixes vs v2:
 *  - Product image: reads data-product-image; falls back to the nearest
 *    <img> inside the card so Horizon lazy-loaded images always resolve.
 *  - Heart icon: uses two separate SVG paths (outline / filled) toggled
 *    via display style — no fill attribute manipulation needed.
 *  - Per-product colouring: only the button for the saved productId
 *    gets the filled heart, not all buttons.
 *  - Popup: all DOM queries re-checked with console.warn so missing
 *    elements are immediately visible in DevTools.
 */

(function () {
  'use strict';

  const LS_KEY = 'jr_wishlists';

  /* ─── Storage ─────────────────────────────────────────────────── */
  function getLists() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
    catch { return []; }
  }

  function saveLists(lists) {
    localStorage.setItem(LS_KEY, JSON.stringify(lists));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { lists } }));
  }

  function uid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  function createList(name) {
    const lists = getLists();
    const list = { id: uid(), name: name.trim(), createdAt: new Date().toISOString(), items: [] };
    lists.unshift(list);
    saveLists(lists);
    return list;
  }

  function deleteList(id) {
    saveLists(getLists().filter(l => l.id !== id));
  }

  function addProductToList(listId, product) {
    const lists = getLists();
    const list = lists.find(l => l.id === listId);
    if (!list) return false;
    if (list.items.some(i => String(i.productId) === String(product.productId))) return false;
    list.items.unshift({ ...product, addedAt: new Date().toISOString() });
    saveLists(lists);
    return true;
  }

  function removeProductFromList(listId, productId) {
    const lists = getLists();
    const list = lists.find(l => l.id === listId);
    if (!list) return;
    list.items = list.items.filter(i => String(i.productId) !== String(productId));
    saveLists(lists);
  }

  function isProductInAnyList(productId) {
    return getLists().some(l => l.items.some(i => String(i.productId) === String(productId)));
  }

  function escHtml(s) {
    return String(s || '')
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  /* ─── Image resolution ────────────────────────────────────────── */
  /**
   * Tries multiple strategies to get a usable image URL from a
   * wishlist trigger button, because Horizon lazy-loads images.
   *
   * Priority:
   *  1. data-product-image on the button (set by wishlist-icon.liquid)
   *  2. The src of the first <img> inside the closest product-card ancestor
   *  3. The data-src of the first <img> (Horizon lazy-load attribute)
   *  4. The srcset first entry
   *  5. Empty string (popup will just show a broken image — acceptable)
   */
  function resolveImage(btn) {
    // 1. Our own data attribute
    const fromData = (btn.dataset.productImage || '').trim();
    if (fromData && !fromData.startsWith('//cdn.shopify.com/s/files/1/0000/')) {
      // Ensure it's a full URL
      return fromData.startsWith('http') ? fromData : 'https:' + fromData;
    }
    if (fromData) return fromData.startsWith('http') ? fromData : 'https:' + fromData;

    // 2–4. Walk up to the product card and find the first product image
    const card = btn.closest('[data-product-id], .card, .product-card, product-card, .card-wrapper, .card__inner, li[data-product]') || btn.parentElement;
    if (!card) return '';

    const img = card.querySelector('img[src]:not([src=""]), img[data-src]:not([data-src=""])');
    if (!img) return '';

    const src = img.getAttribute('src') || '';
    const dataSrc = img.getAttribute('data-src') || '';
    const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset') || '';

    let url = src || dataSrc || (srcset ? srcset.split(',')[0].trim().split(' ')[0] : '');
    if (!url) return '';

    // Upgrade Shopify CDN URLs to a reasonable size if they have a size param
    url = url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master)\./, '_600x600.');
    return url.startsWith('http') ? url : 'https:' + url;
  }

  /* ─── Confirmation popup ──────────────────────────────────────── */
  const Confirm = {
    overlay: null,
    init() {
      this.overlay = document.getElementById('wl-confirm-overlay');
      if (!this.overlay) return;
      document.getElementById('wl-confirm-ok').addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', e => { if (e.target === this.overlay) this.close(); });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close(); });
    },
    open() {
      if (!this.overlay) return;
      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },
    close() {
      if (!this.overlay) return;
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  /* ─── Main popup ──────────────────────────────────────────────── */
  const Popup = {
    overlay: null,
    imgEl: null,
    titleEl: null,
    typeEl: null,
    listsEl: null,
    inputEl: null,
    currentProduct: null,

    init() {
      this.overlay  = document.getElementById('wl-popup-overlay');
      if (!this.overlay) { console.warn('[Wishlist] #wl-popup-overlay not found'); return; }

      this.imgEl    = document.getElementById('wl-popup-img');
      this.titleEl  = document.getElementById('wl-popup-product-title');
      this.typeEl   = document.getElementById('wl-popup-product-type');
      this.listsEl  = document.getElementById('wl-existing-lists');
      this.inputEl  = document.getElementById('wl-new-list-input');

      // Warn for any missing elements so debugging is easy
      ['imgEl','titleEl','typeEl','listsEl','inputEl'].forEach(k => {
        if (!this[k]) console.warn('[Wishlist] Missing element for:', k);
      });

      document.getElementById('wl-popup-close')
        .addEventListener('click', () => this.close());

      this.overlay.addEventListener('click', e => {
        if (e.target === this.overlay) this.close();
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) this.close();
      });

      document.getElementById('wl-new-list-btn')
        .addEventListener('click', () => this.handleCreateAndAdd());

      this.inputEl.addEventListener('keydown', e => {
        if (e.key === 'Enter') this.handleCreateAndAdd();
      });

      // Existing list row → add product → close → confirm
      this.listsEl.addEventListener('click', e => {
        const btn = e.target.closest('.wl-list-row__btn');
        if (!btn || btn.disabled) return;
        if (this.currentProduct) addProductToList(btn.dataset.listId, this.currentProduct);
        this.close();
        Confirm.open();
        updateHeartIcons();
        updateHeaderBadge();
      });
    },

    open(product) {
      this.currentProduct = product;

      // Populate image
      if (this.imgEl) {
        this.imgEl.src = product.image || '';
        this.imgEl.alt = product.title || '';
        // Show a placeholder background if image is empty
        this.imgEl.style.background = product.image ? '' : '#f0ece6';
      }

      // Populate text
      if (this.titleEl) this.titleEl.textContent = product.title || '';
      if (this.typeEl)  this.typeEl.textContent  = product.type  || '';

      if (this.inputEl) this.inputEl.value = '';

      this.renderLists();

      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    },

    renderLists() {
      if (!this.listsEl) return;
      const lists = getLists();
      if (!lists.length) { this.listsEl.innerHTML = ''; return; }

      this.listsEl.innerHTML = lists.map(list => {
        const inList = this.currentProduct &&
          list.items.some(i => String(i.productId) === String(this.currentProduct.productId));
        return `
          <div class="wl-list-row${inList ? ' is-added' : ''}">
            <span class="wl-list-row__name">${escHtml(list.name)}</span>
            <button class="wl-list-row__btn" data-list-id="${list.id}"
              aria-label="${inList ? 'Already in' : 'Add to'} ${escHtml(list.name)}"
              ${inList ? 'disabled' : ''}>
              ${inList
                ? `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7L6 11L12 3" stroke="currentColor" stroke-width="1.5"/></svg>`
                : `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5"/></svg>`
              }
            </button>
          </div>`;
      }).join('');
    },

    handleCreateAndAdd() {
      const name = (this.inputEl ? this.inputEl.value : '').trim();
      if (!name) { if (this.inputEl) this.inputEl.focus(); return; }
      const newList = createList(name);
      if (this.currentProduct) addProductToList(newList.id, this.currentProduct);
      if (this.inputEl) this.inputEl.value = '';
      this.close();
      Confirm.open();
      updateHeartIcons();
      updateHeaderBadge();
    }
  };

  /* ─── Heart icons — PER-PRODUCT colouring ─────────────────────── */
  /**
   * Sets each .js-wishlist-trigger button's visual state individually.
   * - outline path visible, filled path hidden  → not wishlisted
   * - outline path hidden, filled path visible  → wishlisted
   * Uses the two-path SVG structure from wishlist-icon.liquid.
   */
  function setHeartState(btn, wishlisted) {
    const outline = btn.querySelector('.wl-heart-outline');
    const filled  = btn.querySelector('.wl-heart-filled');
    if (wishlisted) {
      btn.classList.add('is-wishlisted');
      if (outline) outline.style.display = 'none';
      if (filled)  filled.style.display  = '';
    } else {
      btn.classList.remove('is-wishlisted');
      if (outline) outline.style.display = '';
      if (filled)  filled.style.display  = 'none';
    }
  }

  function updateHeartIcons() {
    document.querySelectorAll('.js-wishlist-trigger').forEach(btn => {
      const id = btn.dataset.productId;
      setHeartState(btn, isProductInAnyList(id));
    });
  }

  function bindHeartIcons() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('.js-wishlist-trigger');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      // Resolve image at click time (Horizon images are lazy-loaded)
      const image = resolveImage(btn);

      Popup.open({
        productId : btn.dataset.productId,
        title     : btn.dataset.productTitle   || '',
        type      : btn.dataset.productType    || '',
        url       : btn.dataset.productUrl     || window.location.pathname,
        image     : image,
        handle    : btn.dataset.productHandle  || ''
      });
    });
  }

  /* ─── Header badge ────────────────────────────────────────────── */
  function updateHeaderBadge() {
    const badge = document.querySelector('.js-wishlist-header-count');
    if (!badge) return;
    const total = getLists().reduce((s, l) => s + l.items.length, 0);
    badge.textContent = total;
    badge.style.display = total > 0 ? '' : 'none';
  }

  /* ─── Wishlist page ───────────────────────────────────────────── */
  const WishlistPage = {
    pendingDeleteId: null,
    init() {
      if (!document.getElementById('wishlist-page')) return;
      this.render();
      document.addEventListener('wishlist:updated', () => this.render());
      document.getElementById('wishlist-page').addEventListener('click', e => {
        if (e.target.closest('#wishlist-page-create-btn') || e.target.closest('#wishlist-page-create-btn-top'))
          this.openCreateModal();
        const d = e.target.closest('.js-wl-delete-list');
        if (d) this.openDeleteModal(d.dataset.listId);
        const r = e.target.closest('.js-wl-remove-product');
        if (r) removeProductFromList(r.dataset.listId, r.dataset.productId);
      });
      document.getElementById('wishlist-modal-cancel')?.addEventListener('click', () => this.closeCreateModal());
      document.getElementById('wishlist-modal-confirm')?.addEventListener('click', () => this.confirmCreate());
      document.getElementById('wishlist-modal-input')?.addEventListener('keydown', e => { if (e.key === 'Enter') this.confirmCreate(); });
      document.getElementById('wishlist-delete-cancel')?.addEventListener('click', () => this.closeDeleteModal());
      document.getElementById('wishlist-delete-confirm')?.addEventListener('click', () => this.confirmDelete());
    },
    render() {
      const lists = getLists();
      const emptyEl   = document.getElementById('wishlist-page-empty');
      const contentEl = document.getElementById('wishlist-page-content');
      const listsEl   = document.getElementById('wishlist-page-lists');
      if (!emptyEl || !contentEl) return;
      if (!lists.length) { emptyEl.style.display=''; contentEl.style.display='none'; return; }
      emptyEl.style.display='none'; contentEl.style.display='';
      listsEl.innerHTML = lists.map(list => `
        <div class="wishlist-list-group" data-list-id="${list.id}">
          <div class="wishlist-list-group__header">
            <h2 class="wishlist-list-group__name">${escHtml(list.name)}</h2>
            <button class="wishlist-list-group__delete js-wl-delete-list" data-list-id="${list.id}" aria-label="Delete list">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10" stroke="currentColor" stroke-width="1.2"/></svg>
            </button>
          </div>
          <div class="wishlist-list-group__items">
            ${list.items.length ? list.items.map(item => `
              <div class="wishlist-item">
                <a href="${item.url}" class="wishlist-item__image-link">
                  <img src="${escHtml(item.image)}" alt="${escHtml(item.title)}" loading="lazy" class="wishlist-item__image">
                </a>
                <div class="wishlist-item__info">
                  <a href="${item.url}" class="wishlist-item__title">${escHtml(item.title)}</a>
                  <p class="wishlist-item__type">${escHtml(item.type)}</p>
                  <button class="wishlist-item__remove js-wl-remove-product"
                    data-list-id="${list.id}" data-product-id="${item.productId}">Remove</button>
                </div>
              </div>`).join('')
            : '<p class="wishlist-list-group__empty">No items yet.</p>'}
          </div>
        </div>`).join('');
    },
    openCreateModal() {
      document.getElementById('wishlist-modal-input').value = '';
      document.getElementById('wishlist-create-modal').classList.add('is-open');
      document.getElementById('wishlist-modal-input').focus();
    },
    closeCreateModal() { document.getElementById('wishlist-create-modal').classList.remove('is-open'); },
    confirmCreate() {
      const name = document.getElementById('wishlist-modal-input').value.trim();
      if (!name) return;
      createList(name); this.closeCreateModal();
    },
    openDeleteModal(id) {
      this.pendingDeleteId = id;
      document.getElementById('wishlist-delete-modal').classList.add('is-open');
    },
    closeDeleteModal() {
      this.pendingDeleteId = null;
      document.getElementById('wishlist-delete-modal').classList.remove('is-open');
    },
    confirmDelete() {
      if (this.pendingDeleteId) deleteList(this.pendingDeleteId);
      this.closeDeleteModal();
    }
  };

  /* ─── Boot ────────────────────────────────────────────────────── */
  function init() {
    Confirm.init();
    Popup.init();
    bindHeartIcons();
    updateHeartIcons();
    updateHeaderBadge();
    WishlistPage.init();
    document.addEventListener('wishlist:updated', () => {
      updateHeartIcons();
      updateHeaderBadge();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.JRWishlist = { getLists, createList, deleteList, addProductToList, removeProductFromList };

})();

(function () {
  'use strict';

  const LS_KEY = 'jr_wishlists';

  /* ─── Storage helpers ─────────────────────────────────────────── */
  function getLists() {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; }
    catch { return []; }
  }

  function saveLists(lists) {
    localStorage.setItem(LS_KEY, JSON.stringify(lists));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { lists } }));
  }

  function uid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  function createList(name) {
    const lists = getLists();
    const list = { id: uid(), name: name.trim(), createdAt: new Date().toISOString(), items: [] };
    lists.unshift(list);
    saveLists(lists);
    return list;
  }

  function deleteList(listId) {
    saveLists(getLists().filter(l => l.id !== listId));
  }

  function addProductToList(listId, product) {
    const lists = getLists();
    const list = lists.find(l => l.id === listId);
    if (!list) return false;
    if (list.items.some(i => i.productId === product.productId)) return false;
    list.items.unshift({ ...product, addedAt: new Date().toISOString() });
    saveLists(lists);
    return true;
  }

  function removeProductFromList(listId, productId) {
    const lists = getLists();
    const list = lists.find(l => l.id === listId);
    if (!list) return;
    list.items = list.items.filter(i => i.productId !== productId);
    saveLists(lists);
  }

  function isProductInAnyList(productId) {
    return getLists().some(l => l.items.some(i => i.productId === productId));
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ─── Confirmation popup ──────────────────────────────────────── */
  const Confirm = {
    overlay: null,
    init() {
      this.overlay = document.getElementById('wl-confirm-overlay');
      if (!this.overlay) return;
      document.getElementById('wl-confirm-ok')
        .addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });
    },
    open() {
      if (!this.overlay) return;
      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },
    close() {
      if (!this.overlay) return;
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  };

  /* ─── Main popup ──────────────────────────────────────────────── */
  const Popup = {
    overlay: null,
    currentProduct: null,

    init() {
      this.overlay = document.getElementById('wl-popup-overlay');
      if (!this.overlay) return;

      // Close button
      document.getElementById('wl-popup-close')
        .addEventListener('click', () => this.close());

      // Click outside
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      // Create new list → close popup → add product → show confirm
      document.getElementById('wl-new-list-btn')
        .addEventListener('click', () => this.handleCreateAndAdd());

      document.getElementById('wl-new-list-input')
        .addEventListener('keydown', (e) => {
          if (e.key === 'Enter') this.handleCreateAndAdd();
        });

      // Add to existing list → close popup → show confirm
      document.getElementById('wl-existing-lists')
        .addEventListener('click', (e) => {
          const btn = e.target.closest('.wl-list-row__btn');
          if (!btn || btn.disabled) return;
          const listId = btn.dataset.listId;
          if (this.currentProduct) {
            addProductToList(listId, this.currentProduct);
          }
          this.close();
          Confirm.open();
          updateHeartIcons();
          updateHeaderBadge();
        });

      document.addEventListener('wishlist:updated', () => {
        if (this.overlay.classList.contains('is-open')) {
          this.renderLists();
        }
      });
    },

    open(product) {
      this.currentProduct = product;

      // ── Populate product panel ──────────────────────────────────
      const img = document.getElementById('wl-popup-img');
      img.src = product.image || '';
      img.alt = product.title || '';

      document.getElementById('wl-popup-product-title').textContent = product.title || '';
      document.getElementById('wl-popup-product-type').textContent  = product.type  || '';

      // ── Clear input ─────────────────────────────────────────────
      document.getElementById('wl-new-list-input').value = '';

      this.renderLists();

      this.overlay.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.overlay.setAttribute('aria-hidden', 'true');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      // keep currentProduct alive until confirm is dismissed
    },

    renderLists() {
      const container = document.getElementById('wl-existing-lists');
      if (!container) return;
      const lists = getLists();
      if (!lists.length) { container.innerHTML = ''; return; }

      container.innerHTML = lists.map(list => {
        const inList = this.currentProduct &&
          list.items.some(i => i.productId === this.currentProduct.productId);
        return `
          <div class="wl-list-row${inList ? ' is-added' : ''}">
            <span class="wl-list-row__name">${escHtml(list.name)}</span>
            <button
              class="wl-list-row__btn"
              data-list-id="${list.id}"
              aria-label="${inList ? 'Already in ' : 'Add to '}${escHtml(list.name)}"
              ${inList ? 'disabled' : ''}
            >
              ${inList
                ? `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7L6 11L12 3" stroke="currentColor" stroke-width="1.5"/></svg>`
                : `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1V13M1 7H13" stroke="currentColor" stroke-width="1.5"/></svg>`
              }
            </button>
          </div>`;
      }).join('');
    },

    handleCreateAndAdd() {
      const input = document.getElementById('wl-new-list-input');
      const name = input.value.trim();
      if (!name) { input.focus(); return; }

      // Create list first
      const newList = createList(name);

      // Add current product to new list
      if (this.currentProduct) {
        addProductToList(newList.id, this.currentProduct);
      }

      input.value = '';

      // Close main popup → show confirmation
      this.close();
      Confirm.open();
      updateHeartIcons();
      updateHeaderBadge();
    }
  };

  /* ─── Heart icons ─────────────────────────────────────────────── */
  function updateHeartIcons() {
    document.querySelectorAll('.js-wishlist-trigger').forEach(btn => {
      const id = btn.dataset.productId;
      const path = btn.querySelector('.wl-heart-path');
      if (isProductInAnyList(id)) {
        btn.classList.add('is-wishlisted');
        if (path) path.setAttribute('fill', 'currentColor');
      } else {
        btn.classList.remove('is-wishlisted');
        if (path) path.setAttribute('fill', 'none');
      }
    });
  }

  function bindHeartIcons() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-wishlist-trigger');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      Popup.open({
        productId : btn.dataset.productId,
        title     : btn.dataset.productTitle,
        type      : btn.dataset.productType,
        url       : btn.dataset.productUrl,
        image     : btn.dataset.productImage,
        handle    : btn.dataset.productHandle
      });
    });
  }

  /* ─── Header badge ────────────────────────────────────────────── */
  function updateHeaderBadge() {
    const badge = document.querySelector('.js-wishlist-header-count');
    if (!badge) return;
    const total = getLists().reduce((sum, l) => sum + l.items.length, 0);
    badge.textContent = total;
    badge.style.display = total > 0 ? '' : 'none';
  }

  /* ─── Wishlist page ───────────────────────────────────────────── */
  const WishlistPage = {
    pendingDeleteId: null,

    init() {
      const page = document.getElementById('wishlist-page');
      if (!page) return;
      this.render();
      document.addEventListener('wishlist:updated', () => this.render());

      page.addEventListener('click', (e) => {
        if (e.target.closest('#wishlist-page-create-btn') ||
            e.target.closest('#wishlist-page-create-btn-top')) {
          this.openCreateModal();
        }
        const del = e.target.closest('.js-wl-delete-list');
        if (del) this.openDeleteModal(del.dataset.listId);
        const rem = e.target.closest('.js-wl-remove-product');
        if (rem) removeProductFromList(rem.dataset.listId, rem.dataset.productId);
      });

      document.getElementById('wishlist-modal-cancel')
        ?.addEventListener('click', () => this.closeCreateModal());
      document.getElementById('wishlist-modal-confirm')
        ?.addEventListener('click', () => this.confirmCreate());
      document.getElementById('wishlist-modal-input')
        ?.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.confirmCreate(); });
      document.getElementById('wishlist-delete-cancel')
        ?.addEventListener('click', () => this.closeDeleteModal());
      document.getElementById('wishlist-delete-confirm')
        ?.addEventListener('click', () => this.confirmDelete());
    },

    render() {
      const lists    = getLists();
      const emptyEl  = document.getElementById('wishlist-page-empty');
      const contentEl= document.getElementById('wishlist-page-content');
      const listsEl  = document.getElementById('wishlist-page-lists');
      if (!emptyEl || !contentEl) return;

      if (!lists.length) {
        emptyEl.style.display = ''; contentEl.style.display = 'none'; return;
      }
      emptyEl.style.display = 'none'; contentEl.style.display = '';

      listsEl.innerHTML = lists.map(list => `
        <div class="wishlist-list-group" data-list-id="${list.id}">
          <div class="wishlist-list-group__header">
            <h2 class="wishlist-list-group__name">${escHtml(list.name)}</h2>
            <button class="wishlist-list-group__delete js-wl-delete-list" data-list-id="${list.id}" aria-label="Delete list">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10" stroke="currentColor" stroke-width="1.2"/></svg>
            </button>
          </div>
          <div class="wishlist-list-group__items">
            ${list.items.length ? list.items.map(item => `
              <div class="wishlist-item">
                <a href="${item.url}" class="wishlist-item__image-link">
                  <img src="${escHtml(item.image)}" alt="${escHtml(item.title)}" loading="lazy" class="wishlist-item__image">
                </a>
                <div class="wishlist-item__info">
                  <a href="${item.url}" class="wishlist-item__title">${escHtml(item.title)}</a>
                  <p class="wishlist-item__type">${escHtml(item.type)}</p>
                  <button class="wishlist-item__remove js-wl-remove-product"
                    data-list-id="${list.id}" data-product-id="${item.productId}"
                    aria-label="Remove">Remove</button>
                </div>
              </div>`).join('')
            : '<p class="wishlist-list-group__empty">No items in this list yet.</p>'}
          </div>
        </div>`).join('');
    },

    openCreateModal() {
      document.getElementById('wishlist-modal-input').value = '';
      document.getElementById('wishlist-create-modal').classList.add('is-open');
      document.getElementById('wishlist-modal-input').focus();
    },
    closeCreateModal() {
      document.getElementById('wishlist-create-modal').classList.remove('is-open');
    },
    confirmCreate() {
      const name = document.getElementById('wishlist-modal-input').value.trim();
      if (!name) return;
      createList(name);
      this.closeCreateModal();
    },
    openDeleteModal(id) {
      this.pendingDeleteId = id;
      document.getElementById('wishlist-delete-modal').classList.add('is-open');
    },
    closeDeleteModal() {
      this.pendingDeleteId = null;
      document.getElementById('wishlist-delete-modal').classList.remove('is-open');
    },
    confirmDelete() {
      if (this.pendingDeleteId) deleteList(this.pendingDeleteId);
      this.closeDeleteModal();
    }
  };

  /* ─── Boot ────────────────────────────────────────────────────── */
  function init() {
    Confirm.init();
    Popup.init();
    bindHeartIcons();
    updateHeartIcons();
    updateHeaderBadge();
    WishlistPage.init();

    document.addEventListener('wishlist:updated', () => {
      updateHeartIcons();
      updateHeaderBadge();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ─── Public API ──────────────────────────────────────────────── */
  window.JRWishlist = { getLists, createList, deleteList, addProductToList, removeProductFromList };

})();